import React from 'react';
import {connect} from 'react-redux';
import {getCellBgColor} from 'helpers/common-helper';
import PlayersInfo from 'desktop/elements/players/info';
import {RankPlayersList as BasePlayersList} from 'components/organisms/players-list/ranks';
import PlayerLock from 'components/molecules/player-lock';
import Loader from 'components/atoms/loaders/loader';
import RankPlayersGroupExclusionSwitcher from 'components/molecules/player-exclusion/rank-players-group-exclusion-switcher';
import RankPlayerExclusionState from 'components/molecules/player-exclusion/rank-player-exclusion-state';
import PlayerExclusionState from 'components/molecules/player-exclusion/player-exclusion-state';
import Const from 'constants';

class PlayersList extends BasePlayersList {
    componentDidUpdate(prevProps) {
        const {
            userPointsType,
            reset: {settings: resetSettings},
        } = this.props;
        const {userPointsType: prevUPType} = prevProps;

        if (this.wasSettingsReset(resetSettings)) {
            this.updateResetSettingsSignature(resetSettings);
            super.resetBaseState();
        } else if (!userPointsType.isUserPointsTypeEqual(prevUPType.getUserPointsType())) {
            super.resetBaseState({
                sortBy: {...this.state.sortBy},
            });
        }
    }

    getSearchConditions() {
        const {playersFilter} = this.props;

        return {
            ...this.getSearchRuleByMode({
                salaryMode: playersFilter.playersType,
            }),
            ...this.getSearchRuleByPosition(),
            ...this.getSearchRuleByPlayerName(),
            ...this.getSearchRuleByCallback({
                propName: 'positionName',
                callback: (player) => !(player.isConstructor() && playersFilter.playersType === Const.CAPTAIN_MODE),
            }),
        };
    }

    isModeApplicableToPlayer(player, applicableMode, activeMode) {
        if (!player.isConstructor()) {
            return true;
        }

        if (super.isExcludedMode(activeMode) || super.isLockedMode(activeMode)) {
            return applicableMode === Const.FLEX_MODE;
        }

        return true;
    }

    render() {
        const {players, sportConfig, playersFilter} = this.props;

        if (!players.isPlayersGroupReady()) {
            return <Loader />;
        }

        const activeMode = playersFilter.playersType;
        const playersList = this.getPlayersForRendering({
            isModeApplicableToPlayer: this.isModeApplicableToPlayer,
        });

        const rows = [];
        if (playersList.length > 0) {
            let shadingOptions = sportConfig.getShadingOptions();
            playersList.map((player, index) => {
                const userScoreBgColor = getCellBgColor(player.userScore, shadingOptions.userScore);
                const winOddsBgColor = getCellBgColor(player.winOdds, shadingOptions.winOdds);
                const t6OddsBgColor = getCellBgColor(player.t6Odds, shadingOptions.t6Odds);
                const t10OddsBgColor = getCellBgColor(player.t10Odds, shadingOptions.t10Odds);
                const salary = this.getActualValue(player, player.getPropNameOfMode('salary'));

                rows.push(
                    <tr key={index}>
                        <td className="td-chk">
                            {player.isDriver() ? (
                                <RankPlayerExclusionState
                                    activeMode={activeMode}
                                    playerMode={player.getMode()}
                                    playerId={player.getId()}
                                />
                            ) : (
                                <PlayerExclusionState activeMode={activeMode} playerId={player.getId()} />
                            )}
                        </td>
                        <td className="td-lock">
                            {player.isDriver() ? (
                                this.getPlayerLockView({
                                    playerId: player.getId(),
                                    playerMode: player.getMode(),
                                    activeMode,
                                })
                            ) : (
                                <PlayerLock className={`lock-btn`} playerId={player.getId()} />
                            )}
                        </td>
                        <td>
                            <span>{`${player.name}`}</span>
                        </td>
                        <td>{this.getPlayerSympathyComponent({player, activeMode})}</td>
                        <td>{this.getPlayerRankView({activeMode, playerMode: player.getMode()})}</td>
                        <td className="text-capitalize">
                            <span>{player.positionName}</span>
                        </td>
                        <td>
                            <span>{player.team}</span>
                        </td>
                        <td>
                            <span>{salary}</span>
                        </td>
                        <td>
                            <span>{player.starting}</span>
                        </td>
                        <td className="color-cell" style={{...winOddsBgColor}}>
                            <span>{player.winOdds}</span>
                        </td>
                        <td className="color-cell" style={{...t6OddsBgColor}}>
                            <span>{player.t6Odds}</span>
                        </td>
                        <td className="color-cell" style={{...t10OddsBgColor}}>
                            <span>{player.t10Odds}</span>
                        </td>
                        <td className="color-cell" style={{...userScoreBgColor}}>
                            <span>{player.getPropValueOfMode('userScore')}</span>
                        </td>
                        <td>
                            <span>{player.getPropValueOfMode('adminPoints')}</span>
                        </td>
                        {this.getFinalPointsBlock({
                            points: player.getPropValueOfMode('fantasyPoints'),
                        })}
                        <td className="td-narrow">
                            {this.wrapContentInTooltip(
                                this.getPlayerPointsComponent({
                                    player,
                                    activeMode,
                                }),
                                `Default Score: ${player.getPropValueOfMode('adminPoints')}`,
                            )}
                        </td>
                        <td className="td-narrow">
                            {this.getPlayerExposureComponent({
                                playerId: player.getId(),
                                propName: 'exposureMin',
                                mode: player.getMode(),
                            })}
                        </td>
                        <td className="td-narrow">
                            {this.getPlayerMaxExposureComponent({
                                player,
                                activeMode,
                            })}
                        </td>
                    </tr>,
                );
            });
        } else {
            rows.push(
                <tr key="players-not-found">
                    <td colSpan="14" className="text-center">
                        Players were not found.
                    </td>
                </tr>,
            );
        }

        return (
            <div>
                <div className="main-table table-holder table-scroll">
                    <table className="table players-pool">
                        <thead>
                            <tr>
                                <th className="td-chk">
                                    <RankPlayersGroupExclusionSwitcher players={playersList} activeMode={activeMode} />
                                </th>
                                <th colSpan="4">
                                    <a
                                        className={super.getSortDirectionClassName('name')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'name')}
                                    >
                                        Name
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('position')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'position')}
                                    >
                                        Pos
                                    </a>
                                </th>
                                <th className="td-team">
                                    <a
                                        className={super.getSortDirectionClassName('team')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'team')}
                                    >
                                        Team
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('salary')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'salary')}
                                    >
                                        Salary
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('starting')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'starting')}
                                    >
                                        Starting
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('winOdds')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'winOdds')}
                                    >
                                        Win Odds
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('t6Odds')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 't6Odds')}
                                    >
                                        T6 Odds
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('t10Odds')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 't10Odds')}
                                    >
                                        T10 Odds
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('userScore')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'userScore')}
                                    >
                                        Fpts/$
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('adminPoints')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'adminPoints')}
                                    >
                                        Proj
                                    </a>
                                </th>
                                {this.getFinalPointsHeaderSort()}
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('userPoints')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'userPoints')}
                                    >
                                        FPts
                                    </a>
                                </th>
                                <th className="td-per">
                                    <a
                                        className={super.getSortDirectionClassName('exposureMin')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'exposureMin')}
                                    >
                                        Exp. min
                                    </a>
                                    {this.getGeneralExposureComponent({
                                        players: playersList,
                                        propName: 'exposureMin',
                                        mode: activeMode,
                                    })}
                                </th>
                                <th className="td-per">
                                    <a
                                        className={super.getSortDirectionClassName('exposureMax')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'exposureMax')}
                                    >
                                        Exp. max
                                    </a>
                                    {this.getGeneralMaxExposureComponent({
                                        players: playersList,
                                        activeMode,
                                    })}
                                </th>
                            </tr>
                        </thead>

                        <tbody>{rows}</tbody>
                    </table>
                </div>

                <PlayersInfo playersQuantity={playersList.length} />
            </div>
        );
    }
}

function mapStatesToProps(state) {
    const {
        sportConfig,
        reset,
        playersLock,
        userPointsType,
        modesConfig,
        playersData: {
            players,
            filters,
        },
    } = state;

    return {
        players,
        playersFilter: filters,
        playersLock,
        sportConfig,
        reset,
        userPointsType,
        modesConfig,
    };
}

const Table = connect(mapStatesToProps)(PlayersList);

export {Table};
